import { LitElement, html } from 'lit';
import { styles } from './asc-button.styles';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { when } from 'lit/directives/when.js';
import { dispatch } from '../../utils/event-helpers';

export const ASC_BUTTON_TAG = 'asc-button' as const;

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'link-color' | 'link-gray';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';
// Open string union — new themes can be added without a breaking change
export type ButtonTheme =
  | 'default'
  | 'destructive'
  | 'plum'
  | 'berry'
  | 'sky'
  | (string & {});

/**
 * Primary interactive control for triggering actions or navigating to a destination.
 *
 * Supports five visual variants across multiple theme palettes (`default`,
 * `destructive`, `plum`, `berry`, `sky`),
 * four sizes, an icon-only display mode, a loading state with an inline spinner,
 * and an optional link mode (renders an `<a>` element when `href` is supplied).
 * The component is purely presentational — it fires an `asc-click` event and
 * delegates navigation to the browser's native anchor behaviour.
 *
 * To make the button fill its container (e.g. full-width on mobile), set a
 * width or block display on the `<asc-button>` element itself; the inner
 * `<button>` / `<a>` will stretch to match via `width: 100%`.
 *
 * @element asc-button
 *
 * @fires asc-click - Fired on click when not disabled or loading. No detail.
 *
 * @slot - Required. Button label text. In `icon-only` mode, visually hidden but used as accessible name.
 * @slot leading-icon - Optional icon before the label. Accepts an `<svg>` element. Hidden during loading.
 * @slot trailing-icon - Optional icon after the label. Accepts an `<svg>` element. Hidden in `icon-only` mode.
 *
 * @csspart base          - The root `<button>` or `<a>` element.
 * @csspart label         - The `<span>` wrapping the default slot.
 * @csspart leading-icon  - The `<span>` wrapping the leading-icon slot.
 * @csspart trailing-icon - The `<span>` wrapping the trailing-icon slot.
 * @csspart spinner       - The animated loading indicator.
 *
 * @cssprop --asc-button-border-radius                  - Border radius. Default: `var(--asc-radius-sm)` (default theme), `var(--asc-radius-md)` (destructive theme), `var(--asc-radius-xs)` (icon-only mode)
 * @cssprop --asc-button-icon-gap                       - Gap between icon and label. Default: `var(--asc-spacing-xs)` for `size="sm"`/`"md"`, `var(--asc-spacing-sm)` for `size="lg"`/`"xl"`.
 * @cssprop --asc-button-font-size-sm                   - Font size for `size="sm"`. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-button-font-size-md                   - Font size for `size="md"`. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-button-font-size-lg                   - Font size for `size="lg"`. Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-button-font-size-xl                   - Font size for `size="xl"`. Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-button-padding-y-sm                   - Vertical padding for `size="sm"`. Default: `8px`
 * @cssprop --asc-button-padding-x-sm                   - Horizontal padding for `size="sm"`. Default: `12px`
 * @cssprop --asc-button-padding-y-md                   - Vertical padding for `size="md"`. Default: `10px`
 * @cssprop --asc-button-padding-x-md                   - Horizontal padding for `size="md"`. Default: `14px`
 * @cssprop --asc-button-padding-y-lg                   - Vertical padding for `size="lg"`. Default: `10px`
 * @cssprop --asc-button-padding-x-lg                   - Horizontal padding for `size="lg"`. Default: `16px`
 * @cssprop --asc-button-padding-y-xl                   - Vertical padding for `size="xl"`. Default: `12px`
 * @cssprop --asc-button-padding-x-xl                   - Horizontal padding for `size="xl"`. Default: `18px`
 * @cssprop --asc-button-focus-ring                     - Focus ring color (default theme). Default: `var(--asc-focus-ring)`
 * @cssprop --asc-button-focus-ring-destructive         - Focus ring color (destructive theme). Default: `var(--asc-focus-ring-error)`
 * @cssprop --asc-button-focus-ring-separator           - Color of the 2px separator ring between the button edge and the focus ring. Set to `transparent` for a flush ring (used by `asc-button-group`). Default: `white`
 * @cssprop --asc-button-focus-ring-width               - Spread radius of the colored focus ring. Default: `4px`. Set to `2px` alongside a transparent separator for a flush 2px ring (used by `asc-button-group`).
 * @cssprop --asc-button-primary-bg                     - Primary background (default theme). Default: `var(--asc-bg-brand-solid)`
 * @cssprop --asc-button-primary-bg-hover               - Primary background hover (default theme). Default: `var(--asc-bg-brand-solid_hover)`
 * @cssprop --asc-button-primary-bg-destructive         - Primary background (destructive theme). Default: `var(--asc-bg-error-solid)`
 * @cssprop --asc-button-primary-bg-destructive-hover   - Primary background hover (destructive theme). Default: `var(--asc-bg-error-solid_hover)`
 * @cssprop --asc-button-primary-fg                     - Primary foreground (all themes). Default: `var(--asc-color-base-white)`
 * @cssprop --asc-button-primary-fg-destructive         - Primary foreground (destructive theme). Default: `var(--asc-color-base-white)`
 * @cssprop --asc-button-secondary-bg                   - Secondary background (default theme). Default: `var(--asc-bg-primary)`
 * @cssprop --asc-button-secondary-bg-hover             - Secondary background hover (default theme). Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-button-secondary-bg-destructive       - Secondary background (destructive theme). Default: `var(--asc-bg-primary)`
 * @cssprop --asc-button-secondary-bg-destructive-hover - Secondary background hover (destructive theme). Default: `var(--asc-bg-error-primary)`
 * @cssprop --asc-button-secondary-border               - Secondary border (default theme). Default: `var(--asc-border-primary)`
 * @cssprop --asc-button-secondary-border-destructive   - Secondary border (destructive theme). Default: `var(--asc-border-error_subtle)`
 * @cssprop --asc-button-secondary-fg                   - Secondary foreground (default theme). Default: `var(--asc-text-secondary)`
 * @cssprop --asc-button-secondary-fg-hover             - Secondary foreground hover (default theme). Default: `var(--asc-text-secondary_hover)`
 * @cssprop --asc-button-secondary-fg-destructive       - Secondary foreground (destructive theme). Default: `var(--asc-text-error-primary)`
 * @cssprop --asc-button-secondary-fg-destructive-hover - Secondary foreground hover (destructive theme). Default: `var(--asc-text-error-primary_hover)`
 * @cssprop --asc-button-tertiary-bg-hover              - Tertiary background hover (default theme). Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-button-tertiary-bg-destructive-hover  - Tertiary background hover (destructive theme). Default: `var(--asc-bg-error-primary)`
 * @cssprop --asc-button-tertiary-fg                    - Tertiary foreground (default theme). Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-button-tertiary-fg-hover              - Tertiary foreground hover (default theme). Default: `var(--asc-text-tertiary_hover)`
 * @cssprop --asc-button-tertiary-fg-destructive        - Tertiary foreground (destructive theme). Default: `var(--asc-text-error-primary)`
 * @cssprop --asc-button-link-color-fg                  - Link-color foreground. Default: `var(--asc-text-brand-secondary)`
 * @cssprop --asc-button-link-color-fg-hover            - Link-color foreground hover. Default: `var(--asc-text-brand-secondary_hover)`
 * @cssprop --asc-button-link-gray-fg                   - Link-gray foreground. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-button-link-gray-fg-hover             - Link-gray foreground hover. Default: `var(--asc-text-tertiary_hover)`
 * @cssprop --asc-button-disabled-bg                    - Disabled background. Default: `var(--asc-bg-disabled_subtle)`
 * @cssprop --asc-button-disabled-fg                    - Disabled foreground. Default: `var(--asc-text-disabled)`
 * @cssprop --asc-button-disabled-border                - Disabled border. Default: `var(--asc-border-disabled_subtle)` (matches background — visually borderless)
 * @cssprop --asc-button-spinner-color                  - Spinner stroke colour. Default: `var(--asc-fg-brand-secondary-inverted)`. Set automatically per-variant during loading; override to customise.
 */
@customElement(ASC_BUTTON_TAG)
export class AscButton extends LitElement {
  static override styles = [styles];

  /** The visual style of the button. */
  @property({ type: String, reflect: true }) variant: ButtonVariant = 'primary';

  /** The size of the button. Controls padding and font size. */
  @property({ type: String, reflect: true }) size: ButtonSize = 'md';

  /**
  * The color palette theme. Use `'destructive'` for error/danger actions,
  * or accent palettes like `'plum'`, `'berry'`, and `'sky'`.
   * Accepts any string to allow future themes without breaking changes.
   */
  @property({ type: String, reflect: true }) theme: ButtonTheme = 'default';

  /** When true, shows only the leading icon; the label is visually hidden and trailing icon is suppressed. */
  @property({ type: Boolean, reflect: true, attribute: 'icon-only' }) iconOnly = false;

  /** Shows a loading spinner, prevents interaction, and sets `aria-busy="true"`. */
  @property({ type: Boolean, reflect: true }) loading = false;

  /** Screen-reader label announced during the loading state. */
  @property({ type: String, attribute: 'loading-label' }) loadingLabel = 'Loading\u2026';

  /** Disables the button and prevents `asc-click` from firing. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Native button type forwarded to `<button>`. Ignored when `href` is set. */
  @property({ type: String }) type: 'button' | 'submit' | 'reset' = 'button';

  /** When set, renders an `<a>` element and navigates on click. */
  @property({ type: String }) href?: string;

  /** Forwarded to `<a target>`. Only meaningful when `href` is set. */
  @property({ type: String }) target?: string;

  /** Forwarded to `<a rel>`. Only meaningful when `href` is set. */
  @property({ type: String }) rel?: string;

  @state() private _hasLeadingIcon = false;
  @state() private _hasTrailingIcon = false;

  private _handleClick(e: MouseEvent): void {
    if (this.disabled || this.loading) {
      e.preventDefault();
      return;
    }
    dispatch(this, 'asc-click');
  }

  private _handleLeadingIconSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasLeadingIcon = slot.assignedNodes().length > 0;
  }

  private _handleTrailingIconSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasTrailingIcon = slot.assignedNodes().length > 0;
  }

  override render() {
    const isLink = this.href !== undefined;
    const isDisabledOrLoading = this.disabled || this.loading;

    const baseClasses = classMap({
      base: true,
      [`base--${this.variant}`]: true,
      [`base--${this.size}`]: true,
      [`base--theme-${this.theme}`]: true,
      'base--icon-only': this.iconOnly,
      'base--loading': this.loading,
    });

    const innerContent = html`
      ${when(
        this.loading,
        () => html`<span part="spinner" class="spinner" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              opacity="0.3"
              d="M19 10C19 11.1819 18.7672 12.3522 18.3149 13.4442C17.8626 14.5361 17.1997 15.5282 16.364 16.364C15.5282 17.1997 14.5361 17.8626 13.4441 18.3149C12.3522 18.7672 11.1819 19 10 19C8.8181 19 7.64778 18.7672 6.55585 18.3149C5.46392 17.8626 4.47176 17.1997 3.63604 16.364C2.80031 15.5282 2.13738 14.5361 1.68508 13.4441C1.23279 12.3522 1 11.1819 1 10C1 8.8181 1.23279 7.64778 1.68509 6.55585C2.13738 5.46392 2.80031 4.47176 3.63604 3.63604C4.47177 2.80031 5.46392 2.13737 6.55585 1.68508C7.64778 1.23279 8.81811 0.999999 10 1C11.1819 1 12.3522 1.23279 13.4442 1.68509C14.5361 2.13738 15.5282 2.80031 16.364 3.63604C17.1997 4.47177 17.8626 5.46392 18.3149 6.55585C18.7672 7.64778 19 8.81811 19 10Z"
              stroke="var(--asc-button-spinner-color, var(--asc-fg-brand-secondary-inverted))"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 1C11.1819 1 12.3522 1.23279 13.4442 1.68508C14.5361 2.13738 15.5282 2.80031 16.364 3.63604C17.1997 4.47177 17.8626 5.46392 18.3149 6.55585C18.7672 7.64778 19 8.8181 19 10"
              stroke="var(--asc-button-spinner-color, var(--asc-fg-brand-secondary-inverted))"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>`,
      )}
      <span
        part="leading-icon"
        class="leading-icon"
        aria-hidden="true"
        ?hidden=${this.loading || !this._hasLeadingIcon}
      >
        <slot name="leading-icon" @slotchange=${this._handleLeadingIconSlotChange}></slot>
      </span>
      <span
        part="label"
        class=${classMap({ label: true, 'visually-hidden': this.iconOnly })}
      >
        <slot></slot>
      </span>
      <span
        part="trailing-icon"
        class="trailing-icon"
        aria-hidden="true"
        ?hidden=${this.iconOnly || !this._hasTrailingIcon}
      >
        <slot name="trailing-icon" @slotchange=${this._handleTrailingIconSlotChange}></slot>
      </span>
    `;

    // Security: auto-inject rel="noopener noreferrer" on _blank anchors when no
    // explicit rel is provided, preventing tab-nabbing (OWASP A05).
    const safeRel =
      this.target === '_blank' && !this.rel ? 'noopener noreferrer' : this.rel;

    if (isLink) {
      return html`
        <a
          part="base"
          class=${baseClasses}
          href=${ifDefined(isDisabledOrLoading ? undefined : this.href)}
          target=${ifDefined(this.target)}
          rel=${ifDefined(safeRel)}
          aria-disabled=${isDisabledOrLoading ? 'true' : 'false'}
          aria-busy=${this.loading ? 'true' : 'false'}
          aria-label=${ifDefined(this.loading ? this.loadingLabel : undefined)}
          tabindex=${isDisabledOrLoading ? '-1' : '0'}
          @click=${this._handleClick}
        >${innerContent}</a>
      `;
    }

    return html`
      <button
        part="base"
        class=${baseClasses}
        type=${this.type}
        ?disabled=${isDisabledOrLoading}
        aria-disabled=${isDisabledOrLoading ? 'true' : 'false'}
        aria-busy=${this.loading ? 'true' : 'false'}
        aria-label=${ifDefined(this.loading ? this.loadingLabel : undefined)}
        @click=${this._handleClick}
      >${innerContent}</button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [ASC_BUTTON_TAG]: AscButton;
  }
}
